/**
 * FILE: realtime-list-filter.util.ts
 * CHỨC NĂNG: Xác định hội thoại vừa có event realtime (chưa có trong danh sách) có thuộc bộ lọc đang xem không.
 * TÁC DỤNG: Không gọi lại API cho từng event — vừa tốn request, vừa không chắc đúng vì search Elasticsearch
 * có độ trễ refresh. FE tự đánh giá theo cùng điều kiện ConversationMapper::listSplit của BE:
 * - Field định danh (hội thoại, loại, page, khách, bài viết của bình luận) không đổi nên lấy từ payload event.
 * - Bộ lọc theo trạng thái (nhãn, chưa đọc, SĐT, chatbot...) chỉ dùng `filterState` — trạng thái BE đọc lại
 *   sau khi ghi. Payload `conversation` chỉ là phần event ghi (webhook luôn gửi hasPhone=false, không gửi
 *   tagIds) nên không dùng để lọc.
 * - Không đủ dữ liệu → 'unknown': danh sách bỏ qua như FE13 (không chèn đoán), tải lại sẽ thấy nếu khớp.
 */
import {
  CONVERSATION_STATUS,
  CONVERSATION_TYPE,
  IConversation,
  IConversationFilterParam,
  IConversationFilterState,
} from '../models/conversation.model';

export const REALTIME_FILTER_MATCH = {
  MATCH: 'match',
  MISMATCH: 'mismatch',
  UNKNOWN: 'unknown',
} as const;

export type TRealtimeFilterMatch = typeof REALTIME_FILTER_MATCH[keyof typeof REALTIME_FILTER_MATCH];

type TFilterCheck = (
  value: unknown,
  params: IConversationFilterParam,
  conversation: Partial<IConversation>,
  state: IConversationFilterState | null,
) => TRealtimeFilterMatch;

const {MATCH, MISMATCH, UNKNOWN} = REALTIME_FILTER_MATCH;

/**
 * Tham số không tự lọc kết quả: phân trang/sắp xếp, hoặc chỉ có tác dụng cùng tham số khác
 * (totalTag đi với tagIds, adIds đi với fromAds, typeSearch/searchContent đi với customerName).
 */
const NON_FILTER_KEYS = new Set([
  'sort', 'after', 'limit', 'withTotalItems', 'dataBussiness',
  'totalTag', 'adIds', 'typeSearch', 'searchContent', 'shouldCustomerName',
  // Lọc chia hội thoại của quản lý: ConversationListService kiểm tra bằng SplitConversationService.
  'divisionType', 'assigneeIds', 'assignedGroupId',
]);

/** Bộ lọc FE tự đánh giá được. Tham số có giá trị mà không có ở đây (tìm kiếm, báo cáo...) → 'unknown'. */
const FILTER_CHECKS: Record<string, TFilterCheck> = {
  cid: (value, _params, conversation) => String(value) === String(conversation.id) ? MATCH : MISMATCH,
  type: (value, _params, conversation) => matchIdentity(conversation.type, toIdList(value)),
  channelIds: (value, _params, conversation) => matchIdentity(conversation.channel, toIdList(value)),
  filterPageIds: (value, _params, conversation) => matchIdentity(conversation.fanpageId, toIdList(value)),
  psid: (value, _params, conversation) => matchIdentity(conversation.fanpageUserId, toIdList(value)),
  postIds: matchPost,
  postId: matchPost,
  tagIds: matchTags,
  noTag: matchTags,
  fromAds: matchAds,
  // BE lọc status = chưa đọc với mọi giá trị status gửi lên.
  status: (value, _params, _conversation, state) => matchState(value, state, isUnreadState),
  // BE: hasPhone == 'true' → có SĐT, giá trị khác → chưa có SĐT.
  hasPhone: (value, _params, _conversation, state) =>
    matchState(value, state, current => current.hasPhone === (String(value) === 'true')),
  // BE luôn lọc hasReply = false (chưa trả lời) khi có tham số.
  hasReply: (value, _params, _conversation, state) => matchState(value, state, current => current.hasReply === false),
  hasDraftOrder: (value, _params, _conversation, state) => matchState(value, state, current => current.hasDraftOrder === true),
  chatbotStatus: (value, _params, _conversation, state) =>
    matchState(value, state, current => current.chatbotStatus != null && String(current.chatbotStatus) === String(value)),
};

/**
 * Đánh giá hội thoại theo bộ lọc hiện tại: có tham số không khớp → 'mismatch'; còn tham số chưa đủ
 * dữ liệu để đánh giá → 'unknown'; mọi tham số đều khớp → 'match'.
 */
export function matchRealtimeConversationFilter(
  params: IConversationFilterParam,
  conversation: Partial<IConversation>,
  state: IConversationFilterState | null,
): TRealtimeFilterMatch {
  let result: TRealtimeFilterMatch = MATCH;
  for (const [key, value] of Object.entries(params)) {
    if (!hasFilterValue(value) || NON_FILTER_KEYS.has(key)) continue;
    const check = FILTER_CHECKS[key];
    const outcome = check ? check(value, params, conversation, state) : UNKNOWN;
    if (outcome === MISMATCH) return MISMATCH;
    if (outcome === UNKNOWN) result = UNKNOWN;
  }
  return result;
}

/** Tham số rỗng (null, chuỗi rỗng, mảng rỗng) không được gửi lên BE nên không lọc. */
function hasFilterValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return false;
  return !Array.isArray(value) || value.length > 0;
}

/**
 * Giá trị BE nhận có bật bộ lọc không: parseParams gửi boolean dạng 'true'/'false' và PHP coi mọi chuỗi
 * khác '' và '0' là true — nên hasReply=false vẫn bật lọc "chưa trả lời".
 */
function isBackendTruthy(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  const text = String(value);
  return text !== '' && text !== '0';
}

/** Chuẩn hoá danh sách ID: mảng, chuỗi phân tách dấu phẩy hoặc object {id} (adIds). */
function toIdList(value: unknown): string[] {
  const values = Array.isArray(value)
    ? value
    : typeof value === 'string' ? value.split(',') : value === null || value === undefined ? [] : [value];
  return values
    .map(item => item && typeof item === 'object' ? (item as {id?: unknown}).id : item)
    .map(item => String(item ?? '').trim())
    .filter(Boolean);
}

/** Field định danh không đổi theo thời gian nên tin được giá trị trong payload event. */
function matchIdentity(actual: unknown, expected: string[]): TRealtimeFilterMatch {
  if (!expected.length) return MATCH;
  if (actual === null || actual === undefined || actual === '') return UNKNOWN;
  return expected.includes(String(actual)) ? MATCH : MISMATCH;
}

/** Bộ lọc theo trạng thái chỉ đánh giá khi có filterState; field document không có thì term không khớp. */
function matchState(
  value: unknown,
  state: IConversationFilterState | null,
  predicate: (state: IConversationFilterState) => boolean,
): TRealtimeFilterMatch {
  if (!isBackendTruthy(value)) return MATCH;
  if (!state) return UNKNOWN;
  return predicate(state) ? MATCH : MISMATCH;
}

function isUnreadState(state: IConversationFilterState): boolean {
  return Number(state.status) === CONVERSATION_STATUS.UNREAD;
}

/** listSplit: tagIds cần có ít nhất totalTag nhãn trong danh sách, noTag là chưa có nhãn; có cả hai thì thoả một. */
function matchTags(
  _value: unknown,
  params: IConversationFilterParam,
  _conversation: Partial<IConversation>,
  state: IConversationFilterState | null,
): TRealtimeFilterMatch {
  const filterTagIds = toIdList(params.tagIds);
  const noTag = isBackendTruthy(params.noTag);
  if (!filterTagIds.length && !noTag) return MATCH;
  if (!state) return UNKNOWN;

  const tagIds = toIdList(state.tagIds);
  const requiredCount = Math.max(1, Math.trunc(Number(params.totalTag ?? 1)) || 0);
  const hasFilterTags = filterTagIds.length > 0
    && tagIds.filter(id => filterTagIds.includes(id)).length >= requiredCount;
  return hasFilterTags || (noTag && !tagIds.length) ? MATCH : MISMATCH;
}

/**
 * Lọc theo bài viết (BE so fanpagePostId). fanpagePostId của hội thoại bình luận gắn với bài viết từ đầu
 * nên tin được payload; hội thoại loại khác chỉ đánh giá được bằng filterState.
 */
function matchPost(
  _value: unknown,
  params: IConversationFilterParam,
  conversation: Partial<IConversation>,
  state: IConversationFilterState | null,
): TRealtimeFilterMatch {
  const postIds = params['postId'] ? toIdList(params['postId']) : toIdList(params.postIds);
  if (!postIds.length) return MATCH;
  // Chế độ báo cáo BE lọc theo field postIds của document — không đánh giá được.
  if (isBackendTruthy(params.reportFilter)) return UNKNOWN;

  const postId = state
    ? state.fanpagePostId
    : Number(conversation.type) === CONVERSATION_TYPE.COMMENT ? conversation.fanpagePostId : undefined;
  if (!postId) return state ? MISMATCH : UNKNOWN;
  return postIds.includes(String(postId)) ? MATCH : MISMATCH;
}

/** listSplit: có adIds thì khớp adIds hoặc fromAdsId; không có thì hội thoại chỉ cần có nguồn quảng cáo. */
function matchAds(
  value: unknown,
  params: IConversationFilterParam,
  conversation: Partial<IConversation>,
  state: IConversationFilterState | null,
): TRealtimeFilterMatch {
  if (!isBackendTruthy(value)) return MATCH;
  const filterAdIds = toIdList(params.adIds);
  const source = state ?? conversation;
  const adIds = [...toIdList(source.adIds), ...toIdList(source.fromAdsId)];
  const isMatched = filterAdIds.length ? adIds.some(id => filterAdIds.includes(id)) : adIds.length > 0;
  if (isMatched) return MATCH;
  // Payload chỉ có quảng cáo của lần nhắn này, document có thể đã lưu quảng cáo từ trước.
  return state ? MISMATCH : UNKNOWN;
}
